import { Link, type LinkProps } from "react-router";
import { forwardRef, useCallback } from "react";

/**
 * Map of route path prefixes to their dynamic import functions.
 * When a PreloadLink is hovered, the matching module is prefetched
 * so the transition feels instant.
 */
const routeImports: Record<string, () => Promise<unknown>> = {
  "/": () => import("@/pages/Landing"),
  "/login": () => import("@/pages/Login"),
  "/dashboard": () => import("@/pages/Dashboard"),
  "/wizard": () => import("@/pages/GiftWizard"),
  "/contacts": () => import("@/pages/Contacts"),
  "/calendar": () => import("@/pages/Calendar"),
  "/cards": () => import("@/pages/Cards"),
  "/subscription": () => import("@/pages/Subscription"),
  "/pricing": () => import("@/pages/Subscription"),
  "/browse": () => import("@/pages/BrowseGifts"),
  "/experiences": () => import("@/pages/Experiences"),
  "/featured": () => import("@/pages/Featured"),
  "/saved-gifts": () => import("@/pages/SavedGifts"),
  "/gift-history": () => import("@/pages/GiftHistory"),
  "/wishlist": () => import("@/pages/WishList"),
  "/profile": () => import("@/pages/Profile"),
  "/privacy": () => import("@/pages/Privacy"),
  "/terms": () => import("@/pages/Terms"),
  "/contact": () => import("@/pages/ContactPage"),
  "/gifts-for": () => import("@/pages/GiftsFor"),
  "/my-cards": () => import("@/pages/MyCards"),
};

const preloaded = new Set<string>();

function preloadRoute(to: string) {
  const path = typeof to === "string" ? to : "";
  if (preloaded.has(path)) return;

  // Exact match first, then prefix match
  const importer = routeImports[path] || Object.entries(routeImports).find(
    ([prefix]) => prefix !== "/" && path.startsWith(prefix)
  )?.[1];

  if (importer) {
    preloaded.add(path);
    importer();
  }
}

export { preloadRoute };

/**
 * Eagerly preload the most commonly visited routes on app mount.
 * Call once from App.tsx or a top-level effect.
 */
export function preloadCriticalRoutes() {
  // Use requestIdleCallback if available, otherwise setTimeout
  const schedule = typeof requestIdleCallback === "function"
    ? requestIdleCallback
    : (cb: () => void) => setTimeout(cb, 200);

  schedule(() => {
    preloadRoute("/");
    preloadRoute("/dashboard");
    preloadRoute("/login");
  });
}

/**
 * Drop-in replacement for react-router's <Link> that prefetches
 * the target route's component on mouseenter/focus.
 */
export const PreloadLink = forwardRef<HTMLAnchorElement, LinkProps>(
  function PreloadLink({ to, onMouseEnter, onFocus, ...props }, ref) {
    const path = typeof to === "string" ? to : to.pathname || "";

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        preloadRoute(path);
        onMouseEnter?.(e);
      },
      [path, onMouseEnter]
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLAnchorElement>) => {
        preloadRoute(path);
        onFocus?.(e);
      },
      [path, onFocus]
    );

    return (
      <Link
        ref={ref}
        to={to}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        {...props}
      />
    );
  }
);
